import { Button } from "../../components/ui/button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGoogleLogin, TokenResponse } from "@react-oauth/google";
import { useState, useEffect, useRef } from "react";
import { setGluceraAccessToken, setGluceraUserInfo } from "../../utils/storage";
import assessmentStatusService from "../../services/AssessmentStatusService";
import { Activity } from "lucide-react";
import welcomeBg from "../../assets/homebg.png";
import { post } from "@/services/axiosUitils";
import fitbitAuthService from "../../services/FitbitAuthService";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";

interface GoogleLoginResponse {
  success: boolean;
  message: string;
  user: {
    id: string;
    username: string;
    email: string;
    full_name: string;
    first_name: string;
    last_name: string;
    gender: string;
    date_of_birth: string | null;
    is_email_verified: boolean;
    user_type: string;
  };
  tokens: {
    access: string;
    refresh: string;
  };
  provider: string;
}

const OnbordingLoginpage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const hasExchangedFitbit = useRef(false);

  // Fitbit redirect: iframe modal / popup me postMessage, same tab me callback API + navigate
  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    if (!code || !state) return;
    if (window.parent !== window) {
      window.parent.postMessage(
        { type: "FITBIT_CALLBACK", code, state, success: true },
        "*"
      );
      return;
    }
    if (window.opener) {
      window.opener.postMessage(
        { type: "FITBIT_CALLBACK", code, state, success: true },
        window.location.origin
      );
      window.close();
      return;
    }

    if (hasExchangedFitbit.current) return;
    hasExchangedFitbit.current = true;

    // Clear search parameters from URL immediately to prevent other components/renders from reacting
    const newUrl = window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);

    (async () => {
      try {
        console.log("[Fitbit] /fitbit_scan/callback/ API call ho rahi hai...");
        await fitbitAuthService.exchangeCode(code, state);
        console.log("[Fitbit] /fitbit_scan/callback/ API call ho chuki hai.");
        navigate(`/glucera${GluceraNavigationRoutes.FITNESS}`, { replace: true });
      } catch {
        navigate(`/glucera${GluceraNavigationRoutes.fitbitConnectionPage}`, {
          replace: true,
          state: { error: "Failed to connect Fitbit. Please try again." },
        });
      }
    })();
  }, [searchParams, navigate]);

  useEffect(() => {
    // Override body background for this page
    document.body.style.background = "transparent";
    document.body.style.backgroundColor = "transparent";

    return () => {
      // Restore default background when leaving
      document.body.style.background = "";
      document.body.style.backgroundColor = "";
    };
  }, []);

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse: TokenResponse) => {
      setIsLoading(true);
      setError(null);
      try {
        const payload = {
          access_token: tokenResponse.access_token,
          authuser: "1",
          expires_in: "3599",
          scope:
            "email profile https://www.googleapis.com/auth/userinfo.email openid https://www.googleapis.com/auth/userinfo.profile",
          token_type: "Bearer",
        };

        const result = await post<GoogleLoginResponse>(
          "/auth/staticGoogleLogin/",
          payload
        );

        const data = result;

        if (data.success) {
          // Store tokens and user info using common storage functions
          setGluceraAccessToken(data.tokens.access);
          setGluceraUserInfo(data.user);
          localStorage.setItem("glucera_refresh_token", data.tokens.refresh);

          // Also store in main storage for ProtectedRouter
          localStorage.setItem("accessToken", data.tokens.access);
          localStorage.setItem("refreshToken", data.tokens.refresh);
          localStorage.setItem("userInfo", JSON.stringify(data.user));

          console.log("✅ Login successful - Tokens stored in localStorage");
          console.log("Token will persist even after closing the tab/browser");

          try {
            const assessmentStatus =
              await assessmentStatusService.checkAssessmentCompletion(
                data.tokens.access
              );

            if (assessmentStatus.isComplete) {
              navigate("/glucera/homepage", { replace: true });
            } else {
              navigate("/glucera/genderpage", { replace: true });
            }
          } catch (_assessmentError) {
            navigate("/glucera/genderpage", { replace: true });
          }
        } else {
          setError(data.message || "Failed to authenticate with Google");
        }
      } catch (_err: any) {
        console.error("Google OAuth error:", {
          error: _err,
          response: _err?.response?.data,
          status: _err?.response?.status,
        });
        setError(
          _err?.response?.data?.message ||
          "Unable to connect to server. Please check your internet connection and try again."
        );
      } finally {
        setIsLoading(false);
      }
    },
    onError: error => {
      console.error("Google sign in error:", error);
      setError("Google sign in was unsuccessful. Please try again.");
      setIsLoading(false);
    },
  });
  return (
    <div className="h-screen w-screen overflow-hidden">
      <div className="relative mx-auto flex h-full w-full max-w-md flex-col items-center justify-center overflow-hidden bg-black p-4 sm:p-6">
        {/* Blurred Background Image */}
        <div
          className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-sm"
          style={{ backgroundImage: `url(${welcomeBg})` }}
        />

        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/50" />

        <div className="relative z-10 w-full space-y-6 sm:space-y-8">
          {/* Logo */}
          <div className="flex flex-col items-center gap-3 sm:gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-[1.5rem] bg-gradient-primary shadow-primary sm:h-20 sm:w-20 sm:rounded-[2rem]">
              <Activity className="h-8 w-8 text-primary-foreground sm:h-10 sm:w-10" />
            </div>
            <h1 className="text-3xl font-bold text-white drop-shadow-lg sm:text-4xl">
              Bite Balance
            </h1>
          </div>

          {/* Form */}
          <div className="space-y-4 rounded-3xl border border-white/20 bg-white/10 p-4 backdrop-blur-md sm:space-y-6 sm:p-6">
            <div className="text-center">
              <h2 className="mb-1 text-xl font-bold text-white sm:mb-2 sm:text-2xl">
                Welcome
              </h2>
              <p className="text-xs text-white/80 sm:text-sm">
                Sign in to continue your health journey
              </p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <Button
                onClick={() => login()}
                disabled={isLoading}
                className="h-11 w-full rounded-2xl bg-gradient-primary text-sm shadow-primary hover:opacity-90 disabled:opacity-50 sm:h-12 sm:text-base"
              >
                {isLoading ? "Authenticating..." : "Login With Google"}
              </Button>

              {error && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-center">
                  <p className="text-sm font-medium text-destructive">
                    {error}
                  </p>
                </div>
              )}
            </div>
          </div>

          <p className="text-center text-xs font-medium text-white">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnbordingLoginpage;
